import { useEffect, useState } from 'react';
import { subYears, format } from 'date-fns';
import { object, string } from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import FormContext from './ContextForm';

import Swal from 'sweetalert2';

import form from 'utils/formUtil';

import { useVerifyPolicy } from 'api/rapid/postPolicyVerify';
import LayoutForm from 'page/insurance-rapid-form/LayoutForm';
import formUtil from 'utils/formUtil';
import { useRapidCreateOrder } from 'api/rapid/postOrder';
import ConfirmationPage from './ConfirmationPage';

const removeWhiteSpace = (str) => {
  return str.replace(/(\s|,|\.)/g, '');
};

const employeeDefaultScheme = object({
  prefix: form.schema.prefix,
  name: string().required(),
  bd: string().min(3).required(),
  nationality: form.schema.nationality,
  idCard: string().min(3).required(),
  cardType: form.schema.idCardType,
  policyDate: string().nullable(),
});

const employeeScheme = employeeDefaultScheme.shape({
  employer: string().required(),
  employerId: string().nullable(),
  address1: string().required(),
  district: string().required(),
  area: string().required(),
  province: string().required(),
  zipcode: string().required(),
});

const currDate = new Date();
const initialDate = subYears(currDate, 18);

export default function InsuranceRapidForm() {
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [prefix, setPrefix] = useState();
  const [name, setName] = useState('');
  const [bd, setBd] = useState(initialDate);
  const [nationality, setNationality] = useState();
  const [idCard, setIdCard] = useState('');
  const [cardType, setCardType] = useState();
  const [policyDate, setPolicyDate] = useState(currDate);
  const [toggle, setToggle] = useState(false);
  const [employer, setEmployer] = useState('');
  const [employerId, setEmployerId] = useState('');
  const [address1, setAddress1] = useState('');
  const [district, setDistrict] = useState('');
  const [area, setArea] = useState('');
  const [province, setProvince] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [pkg, setPkg] = useState(formUtil.option.pkg[0]);

  const { mutateAsync: verifyOrder } = useVerifyPolicy();
  const { mutateAsync: createOrder } = useRapidCreateOrder();

  const formData = {
    prefix,
    setPrefix,
    name,
    setName,
    bd,
    setBd,
    nationality,
    setNationality,
    idCard,
    setIdCard,
    cardType,
    setCardType,
    policyDate,
    setPolicyDate,
    toggle,
    setToggle,
    employer,
    setEmployer,
    employerId,
    setEmployerId,
    address1,
    setAddress1,
    district,
    setDistrict,
    area,
    setArea,
    province,
    setProvince,
    zipcode,
    setZipcode,
    pkg,
    setPkg,
  };

  useEffect(() => {
    function handleDisabled() {
      if (!disabled) {
        setDisabled(true);
      }
    }
    function handleEnabled() {
      if (disabled) {
        setDisabled(false);
      }
    }

    const data = {
      prefix: prefix?.value,
      name,
      bd,
      nationality: nationality?.value,
      idCard,
      cardType: cardType?.value,
      policyDate,
      pkg: pkg?.value,
    };
    if (process.env.NODE_ENV === 'development') {
      console.log(data);
    }
    if (toggle) {
      employeeDefaultScheme
        .validate(data)
        .then(() => {
          handleEnabled();
        })
        .catch(() => {
          handleDisabled();
        });
    } else {
      // NOTE:  when toggle off validate address also
      employeeScheme
        .validate({ ...data, employer, employerId, address1, district, area, province, zipcode })
        .then(handleEnabled)
        .catch(handleDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefix?.value,
    name,
    bd,
    nationality?.value,
    idCard,
    cardType?.value,
    policyDate,
    toggle,
    employer,
    employerId,
    address1,
    district,
    area,
    province,
    zipcode,
    pkg?.value,
  ]);

  function resetState() {
    setPrefix();
    setName('');
    setBd(initialDate);
    setNationality();
    setIdCard('');
    setCardType();
    setPolicyDate(currDate);
    setEmployer('');
    setEmployerId('');
    setAddress1('');
    setDistrict('');
    setArea('');
    setProvince('');
    setZipcode('');
    setPkg(formUtil.option.pkg[0]);
  }

  const handleSubmit = () => {
    const data = {
      prefix: prefix?.value,
      name,
      bd: format(bd, 'dd/MM/yyyy'),
      nationality: nationality?.value,
      idCard: removeWhiteSpace(idCard),
      cardType: cardType?.value,
      policyDate: format(policyDate, 'dd/MM/yyyy'),
      employer,
      employerId,
      address1,
      district,
      area,
      province,
      zipcode,
      status: 'create',
      docType: 'form',
      pkg: pkg?.value,
    };

    const confirmSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
        cancelButton: 'button button-error',
      },
      buttonsStyling: false,
    });

    confirmSwal
      .fire({
        title: 'ยืนยันการซื้อ?',
        text: 'เพื่อทำการชำระเงิน',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      })
      .then((result) => {
        if (result.isConfirmed) {
          verifyPopup(data)
            .then((res) => {
              // NOTE: navigate here
              // resetState();
              createOrder({ data: res })
                .then((resp) => {
                  Swal.fire('สำเร็จ', 'ทำการชำระเงิน', 'success');
                  navigate('/rapid/payment/' + resp.invoiceId);
                })
                .catch((err) => {
                  if (axios.isAxiosError(err)) {
                    console.log(err.response);
                    Swal.fire('ผิดพลาด', err.response.data, 'error');
                  } else {
                    Swal.fire('ผิดพลาด', err?.message, 'error');
                  }
                });
            })
            .catch((err) => {
              if (axios.isAxiosError(err)) {
                console.log(err.response);
                Swal.fire('ผิดพลาด', err.response.data, 'error');
              } else {
                Swal.fire('ผิดพลาด', err?.message, 'error');
              }
            });
        }
      });
  };

  async function verifyPopup(data) {
    Swal.fire({
      title: 'Auto close alert!',
      html: 'I will close in <b></b> milliseconds.',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    return await verifyOrder([data])
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        Swal.close();
      });
  }

  return (
    <FormContext.Provider
      value={{ disabled, pageIndex, setPageIndex, setDisabled, handleSubmit, formData, resetState }}
    >
      {pageIndex === 0 ? <LayoutForm /> : <ConfirmationPage />}
    </FormContext.Provider>
  );
}
